import { useEffect } from 'react';

import {
  alpha,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { FormikProvider, useFormik } from 'formik';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';

import DrawerBackHeader from 'components/DrawerBackHeader';
import { DOCUMENT_TYPES_OPTIONS, DocumentType } from 'constants/document';
import { useAppDispatch, useAppSelector } from 'store';
import {
  createFunnelStageOpportunity,
  CreateFunnelStageOpportunityPayload,
  fetchFunnelsSelect,
  fetchFunnelStagesSelect,
  fetchFunnelStageStatus,
  fetchFunnelTags,
  fetchOpportunitySources,
  Funnel,
  funnelsSelector,
  FunnelStage,
  FunnelStageStatus,
  FunnelTag,
  OpportunitySource,
} from 'store/reducers/funnels';
import { currencyBrlMask, parseCurrencyBRLToNumber } from 'utils/currencyUtils';
import { documentMask } from 'utils/documentMask';
import emptyStringToNull from 'utils/emptyStringToNull';

const useStyles = makeStyles(({ appDrawer, palette, spacing }) => ({
  drawer: {
    width: appDrawer.minWidth,
  },
  drawerPaper: {
    width: appDrawer.minWidth,
    flexShrink: 0,
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(3),
  },
  divider: {
    margin: spacing(1, -3),
  },
  sectionTitle: {
    fontWeight: 500,
    color: alpha(palette.primary.main, 0.7),
  },
  documentTypeAdornment: {
    marginRight: spacing(2),
  },
  actions: {
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
  },
}));

const createOpportunityValidationSchema = yup.object({
  funnel: yup.object().nullable().required('O funil é obrigatório'),
  stage: yup.object().nullable().required('O estágio é obrigatório'),
  stageStatus: yup.object().nullable(),
  description: yup.string().optional(),
  value: yup.string().optional(),
  source: yup.object().nullable(),
  tag: yup.object().nullable(),
  businessName: yup.string().optional(),
  documentType: yup.mixed<DocumentType>(),
  documentNumber: yup.string().when('documentType', {
    is: (value: DocumentType) => !!value,
    then: schema => schema.required('O número do documento é obrigatório'),
    otherwise: schema => schema.notRequired(),
  }),
});

type CreateOpportunityFormValues = {
  funnel: Funnel | null;
  stage: FunnelStage | null;
  stageStatus: FunnelStageStatus | null;
  description: string;
  value: string;
  source: OpportunitySource | null;
  tags: FunnelTag[];
  businessName: string;
  documentType: string;
  documentNumber: string;
};

type CreateOpportunityForContactDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  contatoId: string;
};

function CreateOpportunityForContactDrawer(
  props: CreateOpportunityForContactDrawerProps,
) {
  const { open, onClose, contatoId, onSuccess } = props;
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();

  const {
    stageStatus,
    opportunitySources,
    funnelTags,
    funnelStages,
    funnels,
    loadingFunnels,
    loadingFunnelStages,
    loadingFunnelTags,
    loadingOpportunitySources,
    loadingStageStatus,
  } = useAppSelector(funnelsSelector);

  const formik = useFormik<CreateOpportunityFormValues>({
    initialValues: {
      funnel: null,
      stage: null,
      stageStatus: null,
      description: '',
      value: '',
      source: null,
      tags: [],
      businessName: '',
      documentType: '',
      documentNumber: '',
    },
    validationSchema: createOpportunityValidationSchema,
    onSubmit: async (values: CreateOpportunityFormValues) => {
      const { funnel, stage } = values;
      const funnelId = funnel?.id;
      const stageId = stage?.id;

      if (!funnelId || !stageId) {
        return;
      }

      const payload: CreateFunnelStageOpportunityPayload = {
        contactId: contatoId,
        funnelId,
        stageId,
        statusId: emptyStringToNull(values.stageStatus?.id),
        businessName: emptyStringToNull(values.businessName),
        documentType: emptyStringToNull(values.documentType),
        documentNumber: emptyStringToNull(values.documentNumber),
        value: values.value ? parseCurrencyBRLToNumber(values.value) : null,
        description: emptyStringToNull(values.description),
        sourceId: emptyStringToNull(values.source?.id),
        tagIds: values.tags.map(tag => tag.id),
      };

      await dispatch(createFunnelStageOpportunity(payload))
        .unwrap()
        .then(() => {
          enqueueSnackbar('Oportunidade criada com sucesso.');
          if (onSuccess) onSuccess();
          onClose();
          formik.resetForm();
        })
        .catch(error => {
          if (error.response) {
            enqueueSnackbar(error.response.data.message);
          }
        });
    },
  });

  const funnelError = formik.touched.funnel && formik.errors.funnel;
  const stageError = formik.touched.stage && formik.errors.stage;
  const descriptionError =
    formik.touched.description && formik.errors.description;
  const valueError = formik.touched.value && formik.errors.value;
  const documentNumberError =
    formik.touched.documentNumber && formik.errors.documentNumber;

  const handleFunnelChange = (newValue: Funnel | null) => {
    if (newValue) {
      formik.setFieldValue('funnel', newValue);
      formik.setFieldValue('stage', null);
      formik.setFieldValue('stageStatus', null);
      formik.setFieldValue('tags', []);
      dispatch(fetchFunnelTags({ funnelId: newValue.id }));
    }
  };

  const handleClearFunnel = () => {
    formik.setFieldValue('funnel', null);
  };

  const handleStageChange = (newValue: FunnelStage | null) => {
    if (newValue) {
      formik.setFieldValue('stage', newValue);
      formik.setFieldValue('stageStatus', null);
    }
  };

  const handleClearStage = () => {
    formik.setFieldValue('stage', null);
  };

  const handleStageOpen = () => {
    const funnelId = formik.values.funnel?.id;
    if (funnelId) {
      dispatch(fetchFunnelStagesSelect({ funnelId }));
    }
  };

  const handleStatusChange = (newValue: FunnelStageStatus | null) => {
    formik.setFieldValue('stageStatus', newValue);
  };

  const handleClearStatus = () => {
    formik.setFieldValue('stageStatus', null);
  };

  const handleStatusOpen = () => {
    const stageId = formik.values.stage?.id;
    const funnelId = formik.values.funnel?.id;

    if (stageId && funnelId) {
      dispatch(fetchFunnelStageStatus({ funnelId, stageId }));
    }
  };

  const handleSourceChange = (newValue: OpportunitySource | null) => {
    formik.setFieldValue('source', newValue);
  };

  const handleClearSource = () => {
    formik.setFieldValue('source', null);
  };

  const handleTagsChange = (newValue: FunnelTag[]) => {
    formik.setFieldValue('tags', newValue);
  };

  const handleClearTags = () => {
    formik.setFieldValue('tags', []);
  };

  const handleClearDocumentType = () => {
    formik.setFieldValue('documentType', '');
    formik.setFieldValue('documentNumber', '');
  };

  const handleClose = () => {
    if (formik.dirty) {
      confirm({
        title: (
          <Typography variant="body2">Descartar nova oportunidade?</Typography>
        ),
        description: (
          <Typography variant="caption">
            Você está saindo da criação de uma nova oportunidade. Todas as
            informações inseridas até agora serão perdidas. <br /> Deseja
            descartar mesmo assim?
          </Typography>
        ),
        cancellationText: 'Continuar editando',
        confirmationText: 'Sim, descartar',
        confirmationButtonProps: { variant: 'contained', color: 'secondary' },
        dialogProps: { maxWidth: 'xs' },
      })
        .then(() => {
          onClose();
        })
        .catch(() => {
          //
        });
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (open) {
      dispatch(fetchOpportunitySources());
      dispatch(fetchFunnelsSelect());
    }
  }, [open, dispatch]);

  return (
    <Drawer
      transitionDuration={0}
      open={open}
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
      anchor="right"
      ModalProps={{
        BackdropProps: {
          style: {
            backgroundColor: 'transparent',
          },
        },
      }}
    >
      <DrawerBackHeader title="Criar nova oportunidade" onBack={handleClose} />

      <FormikProvider value={formik}>
        <DialogContent className={classes.content}>
          {/* FUNIL SECTION */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" className={classes.sectionTitle}>
                Funil
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                id="funnelId"
                options={funnels || []}
                loading={loadingFunnels}
                getOptionLabel={option => option.name}
                getOptionSelected={(option, value) => option.id === value.id}
                noOptionsText={
                  <Typography variant="body2">
                    Nenhum funil encontrado
                  </Typography>
                }
                value={formik.values.funnel}
                onChange={(_, newValue, reason) => {
                  if (newValue) handleFunnelChange(newValue);
                  if (reason === 'clear') handleClearFunnel();
                }}
                clearText="Limpar"
                closeText="Fechar"
                openText="Abrir"
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Funil"
                    variant="outlined"
                    placeholder="Pesquise"
                    error={Boolean(funnelError)}
                    helperText={funnelError}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                id="stage"
                options={funnelStages || []}
                loading={loadingFunnelStages}
                disabled={!formik.values.funnel}
                getOptionLabel={option => option.name}
                getOptionSelected={(option, value) => option.id === value.id}
                value={formik.values.stage}
                onChange={(_, newValue, reason) => {
                  if (newValue) handleStageChange(newValue);
                  if (reason === 'clear') handleClearStage();
                }}
                clearText="Limpar"
                closeText="Fechar"
                openText="Abrir"
                noOptionsText={
                  <Typography variant="body2">
                    Nenhuma etapa encontrada
                  </Typography>
                }
                onOpen={handleStageOpen}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Etapa do funil"
                    variant="outlined"
                    placeholder="Pesquise"
                    error={Boolean(stageError)}
                    helperText={stageError}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingFunnelStages && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                id="stageStatus"
                options={stageStatus || []}
                loading={loadingStageStatus}
                disabled={!formik.values.stage}
                getOptionLabel={option => option.name}
                getOptionSelected={(option, value) => option.id === value.id}
                value={formik.values.stageStatus}
                onChange={(_, newValue, reason) => {
                  if (newValue) handleStatusChange(newValue);
                  if (reason === 'clear') handleClearStatus();
                }}
                noOptionsText={
                  <Typography variant="body2">
                    Nenhum status encontrado
                  </Typography>
                }
                clearText="Limpar"
                closeText="Fechar"
                openText="Abrir"
                onOpen={handleStatusOpen}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Status"
                    variant="outlined"
                    placeholder="Pesquise"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingStageStatus && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* DETALHES DA NEGOCIAÇÃO */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" className={classes.sectionTitle}>
                Detalhes da negociação
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                name="description"
                label="Descrição"
                placeholder="Descreva os detalhes da negociação"
                multiline
                minRows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                error={Boolean(descriptionError)}
                helperText={descriptionError}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                name="value"
                label="Valor"
                placeholder="R$ 0,00"
                value={formik.values.value}
                onChange={event => {
                  const masked = currencyBrlMask(event.target.value);
                  formik.setFieldValue('value', masked);
                }}
                error={Boolean(valueError)}
                helperText={valueError}
              />
            </Grid>
          </Grid>

          <Divider className={classes.divider} />

          {/* INFORMAÇÕES DA OPORTUNIDADE */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" className={classes.sectionTitle}>
                Informações da oportunidade
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                id="sourceId"
                options={opportunitySources || []}
                loading={loadingOpportunitySources}
                getOptionLabel={option => option.name}
                getOptionSelected={(option, value) => option.id === value.id}
                value={formik.values.source}
                onChange={(_, newValue, reason) => {
                  if (newValue) handleSourceChange(newValue);
                  if (reason === 'clear') handleClearSource();
                }}
                noOptionsText={
                  <Typography variant="body2">
                    Nenhuma origem encontrada
                  </Typography>
                }
                clearText="Limpar"
                closeText="Fechar"
                openText="Abrir"
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Origem do lead"
                    placeholder="Pesquise"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingOpportunitySources && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="tags"
                options={funnelTags || []}
                loading={loadingFunnelTags}
                disabled={!formik.values.funnel}
                getOptionLabel={option => option.name}
                getOptionSelected={(option, value) => option.id === value.id}
                value={formik.values.tags}
                onChange={(_, newValue, reason) => {
                  if (newValue) handleTagsChange(newValue);
                  if (reason === 'clear') handleClearTags();
                }}
                noOptionsText={
                  <Typography variant="body2">
                    Nenhuma tag encontrada
                  </Typography>
                }
                clearText="Limpar"
                closeText="Fechar"
                openText="Abrir"
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Tags da oportunidade"
                    placeholder="Pesquise"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingFunnelTags && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="businessName"
                label="Negócio"
                variant="outlined"
                placeholder="Digite"
                value={formik.values.businessName}
                onChange={formik.handleChange}
              />
            </Grid>

            {/* DOCUMENTO */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    select
                    variant="outlined"
                    name="documentType"
                    placeholder="Tipo"
                    label="Documento"
                    value={formik.values.documentType || ''}
                    onChange={event => {
                      formik.handleChange(event);
                      formik.setFieldValue('documentNumber', '');
                    }}
                    InputProps={{
                      endAdornment: formik.values.documentType && (
                        <InputAdornment
                          className={classes.documentTypeAdornment}
                          position="end"
                        >
                          <IconButton
                            size="small"
                            onClick={handleClearDocumentType}
                            onMouseDown={e => e.preventDefault()}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  >
                    {DOCUMENT_TYPES_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="documentNumber"
                    label="Número do documento"
                    placeholder="Digite o número"
                    value={formik.values.documentNumber}
                    onChange={event => {
                      const masked = documentMask(
                        event.target.value,
                        formik.values.documentType as DocumentType,
                      );
                      formik.setFieldValue('documentNumber', masked);
                    }}
                    error={Boolean(documentNumberError)}
                    helperText={documentNumberError}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions className={classes.actions}>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            disabled={formik.isSubmitting}
            variant="contained"
            color="primary"
            onClick={formik.submitForm}
          >
            Criar oportunidade
          </Button>
        </DialogActions>
      </FormikProvider>
    </Drawer>
  );
}

export default CreateOpportunityForContactDrawer;
